import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { isCustomerPickupOrder } from '@/lib/customerPickup';
import { useQuery, useMutation, gql } from '@apollo/client';
import SalesOrderShipping from './SalesOrderShipping';
import ApiError from '@/lib/shared-coms/ApiError';
import { Spinner } from 'equasion-lib';
import { useRootStore } from '@/lib/providers/RootProvider';
import { WAREHOUSE_SHIPPING_LANES, selectedLane } from '@/lib/shippingLane';

const CLIENT_ORDER = gql`
  query salesOrder($id: ID!, $vendorId: ID!) {
    salesOrder(id: $id, vendorId: $vendorId) {
      id
      vendorSalesOrderNumber
      vendorStatus
      # Customer Pickup: never shipped from here (no label, carrier or tracking) — see below.
      customerPickup {
        enabled
      }
      # A destruction order files no SSCC label at ship, so going home never waits on one.
      isDestructionOrder
      # Bundled shipping — the ONLY signal that this order has been set aside (vendorStatus stays
      # 'shipping'). Drives which primary action the page renders.
      shippingBundleId
      client {
        id
        companyName
      }
      metadata {
        vendorShippingLocation {
          id
          gcp
        }
      }
      # Quoted freight (or prior manual) charge in dollars via USCurrency — seeds the ship form.
      shippingAmount
      shippingMethod {
        id
        name
        # Drives the manual cost/weight ship form: a 'freight' method buys no carrier label.
        carrier
      }
      orderItems {
        id
        quantity
        product {
          id
          title
          fdaProduct {
            id
            name
            elevenDigitNdc
            deaSchedule
          }
        }
      }
    }
    # Membership only — set-aside / join / remove live on the order-summary step. This page only
    # needs to know whether THIS order is already in a bundle so it can ship the whole parcel.
    shippingBundleContext(companyId: $vendorId, salesOrderId: $id) {
      bundle {
        id
        bundleNumber
        status
        orders {
          id
          vendorSalesOrderNumber
          vendorStatus
          itemCount
          shippingMethodName
        }
      }
    }
    company(id: $vendorId) {
      # Normalizes this to Company:<id>. Without it Apollo caches the object inline under
      # ROOT_QUERY.company({"id":X}), which now collides with the normalized Reference written by
      # SalesOrderPicking / AddTote for the same field key.
      id
      integrationSettings {
        shippingDimensionOptions
        fobShippingEnabled
      }
      locations {
        id
        gcp
        type
        isDefaultSalesOrder
        companyName
        address {
          lineOne
          city
          state
        }
      }
    }
    shippingMethods(companyId: $vendorId) {
      id
      name
      carrier
    }
  }
`;

export const ORDER_SHIP = gql`
  mutation orderShip(
    $packages: [PackageAttributes!]
    $id: ID!
    $shippingMethodId: ID
    $companyId: ID!
    $shippingAmount: Float
    $shippingWeight: Float
    $trackingNumber: String
  ) {
    orderShip(
      packages: $packages
      id: $id
      shippingMethodId: $shippingMethodId
      companyId: $companyId
      shippingAmount: $shippingAmount
      shippingWeight: $shippingWeight
      trackingNumber: $trackingNumber
    ) {
      id
    }
  }
`;

const UPDATE_VENDOR_SHIPPING_LOCATION = gql`
  mutation salesOrderUpdateVendorShippingLocation(
    $orderId: ID!
    $companyId: ID!
    $vendorShippingLocationId: ID!
  ) {
    salesOrderUpdateVendorShippingLocation(
      orderId: $orderId
      companyId: $companyId
      vendorShippingLocationId: $vendorShippingLocationId
    ) {
      id
      metadata {
        vendorShippingLocation {
          id
          gcp
        }
      }
    }
  }
`;

export const BUNDLE_SHIP = gql`
  mutation shippingBundleShip(
    $companyId: ID!
    $shippingBundleId: ID!
    $packages: [PackageAttributes!]
    $shippingMethodId: ID
  ) {
    shippingBundleShip(
      companyId: $companyId
      shippingBundleId: $shippingBundleId
      packages: $packages
      shippingMethodId: $shippingMethodId
    ) {
      bundle {
        id
        bundleNumber
        status
      }
      orders {
        id
        vendorStatus
      }
      trackingNumbers
    }
  }
`;

/**
 * Package sheet: a size typed at packing joins the vendor's package library (and is selected). The
 * company's integrationSettings come back with every field CLIENT_ORDER reads, so the cached
 * Company updates in place and the size list shows the new size from then on.
 */
export const ADD_SHIPPING_SIZE = gql`
  mutation companyShippingDimensionOptionAdd($companyId: ID!, $dimensions: [Float!]!) {
    companyShippingDimensionOptionAdd(companyId: $companyId, dimensions: $dimensions) {
      added
      dimensions
      company {
        id
        integrationSettings {
          shippingDimensionOptions
          fobShippingEnabled
        }
      }
    }
  }
`;

const SalesOrderShippingData = (props) => {
  const [orderShip] = useMutation(ORDER_SHIP);
  const [addShippingSize] = useMutation(ADD_SHIPPING_SIZE);
  const [updateVendorShippingLocation] = useMutation(UPDATE_VENDOR_SHIPPING_LOCATION);
  const [bundleShip] = useMutation(BUNDLE_SHIP);
  const { id } = useParams();
  const vendorId = useRootStore((state) => state.selectedCompany.id);

  const { data, error, loading } = useQuery(CLIENT_ORDER, {
    variables: { id, vendorId },
    fetchPolicy: 'network-only'
  });
  // Kept apart from the order query: without a lane the page works exactly as before.
  const lanes = useQuery(WAREHOUSE_SHIPPING_LANES, {
    variables: { companyId: vendorId },
    fetchPolicy: 'network-only',
    skip: !vendorId
  });

  if (loading) return <Spinner />;
  if (error) return <ApiError error={error} />;

  const { salesOrder, shippingBundleContext, company, shippingMethods } = data || {};
  // A Customer Pickup order skips the Shipping step: from a deep link, a stale tab or a bookmark,
  // it goes to its summary, which records the pickup. (The server refuses a carrier ship anyway.)
  if (isCustomerPickupOrder(salesOrder)) {
    return <Navigate to={`/sales-order-summary/${salesOrder.id}`} replace />;
  }
  const { shippingDimensionOptions, fobShippingEnabled } = company.integrationSettings;
  const vendorShippingLocations = (company.locations || []).filter(
    (l) => l.type === 'shipping' && l.gcp
  );

  return (
    <SalesOrderShipping
      salesOrder={salesOrder}
      bundleContext={shippingBundleContext}
      orderShip={orderShip}
      bundleShip={bundleShip}
      addShippingSize={addShippingSize}
      shippingDimensionOptions={shippingDimensionOptions}
      shippingMethods={shippingMethods}
      vendorShippingLocations={vendorShippingLocations}
      updateVendorShippingLocation={updateVendorShippingLocation}
      fobEnabled={fobShippingEnabled === true}
      shippingLane={selectedLane(
        lanes.data?.warehouseShippingLanes ?? [],
        lanes.data?.myShippingLane
      )}
      {...props}
    />
  );
};

export default SalesOrderShippingData;
